import { useAnimations } from "@react-three/drei";
import { useLoader } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { SkeletonUtils } from "three-stdlib";
import { handleAnimationTransition } from "../helpers/avatar-animations";
import { detectMorphScheme, MorphScheme } from "../helpers/detect-morph-scheme";
import { SCHEME_PROFILES } from "../helpers/scheme-profiles";
import { getAuthHeaders } from "../../common/helpers";

const configureGLTFLoader = (loader: GLTFLoader) => {
  (loader as any).setMeshoptDecoder(MeshoptDecoder);
  loader.setRequestHeader(getAuthHeaders());
};

interface IGLTF {
  url: string;
  actionsCallback: (actions: Array<any>) => void;
  setMorphTarget?: (morphTarget: any) => void;
  isBackground: boolean;
  avatarSettings?: any;
  onSchemeDetected?: (scheme: MorphScheme) => void;
}

export function GLTFModelLoader({
  url,
  avatarSettings = {},
  actionsCallback,
  setMorphTarget = () => {},
  isBackground,
  onSchemeDetected,
}: IGLTF) {
  const gltf = useLoader(GLTFLoader, url, configureGLTFLoader);
  const groupRef = useRef<any>(null);
  const schemeRef = useRef<MorphScheme>("LEGACY");
  const [scheme, setScheme] = useState<MorphScheme>("LEGACY");
  const profile = SCHEME_PROFILES[scheme];
  const [position, setPosition] = useState<[number, number, number]>(
    SCHEME_PROFILES.LEGACY.defaultPosition
  );

  const clonedScene = useMemo(() => {
    if (!gltf?.scene) return null;
    return SkeletonUtils.clone(gltf.scene);
  }, [gltf]);

  const { actions } = useAnimations(gltf.animations || [], groupRef) as any;

  useEffect(() => {
    const fallback = SCHEME_PROFILES[schemeRef.current].defaultPosition;
    if (!isBackground) {
      const rawPosition = avatarSettings?.positionSettings;

      if (typeof rawPosition === "string" && rawPosition.trim()) {
        const parsed = rawPosition
          .split(",")
          .map((value: string) => Number(value.trim()));

        if (
          parsed.length === 3 &&
          parsed.every((value: number) => Number.isFinite(value))
        ) {
          setPosition(parsed as [number, number, number]);
        } else {
          setPosition(fallback);
        }
      } else {
        setPosition(fallback);
      }
    } else {
      setPosition(fallback);
    }
  }, [avatarSettings?.positionSettings, isBackground, url, scheme]);

  useEffect(() => {
    if (!clonedScene) return;

    const detected = detectMorphScheme(clonedScene);
    schemeRef.current = detected;
    setScheme(detected);
    onSchemeDetected?.(detected);

    setMorphTarget(clonedScene);

    return () => {
      setMorphTarget(null);
      onSchemeDetected?.("LEGACY");
    };
  }, [clonedScene, setMorphTarget, onSchemeDetected]);

  useEffect(() => {
    if (!actions) return;

    actionsCallback(actions);

    const idleName = profile.clips.idle;
    if (actions[idleName]) {
      handleAnimationTransition(actions, idleName, {
        alsoPlay: profile.enableBlinking ? [profile.blinkingClip] : undefined,
      });
    }

    return () => {
      Object.values(actions).forEach((action: any) => {
        try {
          action?.stop?.();
          action?.reset?.();
        } catch (error) {
          // ignore cleanup errors
        }
      });
    };
  }, [
    actions,
    actionsCallback,
    profile.blinkingClip,
    profile.clips.idle,
    profile.enableBlinking,
  ]);

  if (!clonedScene) {
    return null;
  }

  return (
    <group ref={groupRef}>
      <primitive
        object={clonedScene}
        scale={[profile.scale, profile.scale, profile.scale]}
        position={position}
      />
    </group>
  );
}
